import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, Send, Loader2, LifeBuoy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL__;

const CATEGORIES = [
  { value: 'bug', label: 'Bug or technical issue' },
  { value: 'billing', label: 'Billing / subscription' },
  { value: 'account', label: 'Account access' },
  { value: 'content', label: 'Content / listing' },
  { value: 'other', label: 'Other' },
];

export default function ReportIssueModal({ onClose, initialContext = null }) {
  const [category, setCategory] = useState(initialContext?.category || 'bug');
  const [subject, setSubject] = useState(initialContext?.subject || '');
  const [message, setMessage] = useState(initialContext?.message || '');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 3) {
      toast.error('Please enter a short subject (min. 3 characters)');
      return;
    }
    if (message.trim().length < 5) {
      toast.error('Please describe the issue (min. 5 characters)');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/support/issues`,
        {
          category,
          subject: subject.trim(),
          message: message.trim(),
          page_url: window.location.href,
          context: initialContext || {},
        },
        { withCredentials: true }
      );
      toast.success('Thanks — your issue has been submitted');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[210] flex items-center justify-center p-4"
      data-ccuk-mobile-dialog-root="true" onClick={onClose}>
      <div className="bg-theme-surface border border-theme rounded-xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-theme">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#002FA7]/15 border border-[#002FA7]/30 flex items-center justify-center">
              <LifeBuoy className="h-5 w-5 text-[#002FA7]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-theme-text">Report an issue</h2>
              <p className="text-xs text-theme-muted">Our admin team will follow up as soon as possible.</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-theme-muted hover:text-theme-text">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-theme-text text-sm">Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg bg-theme/50 border border-theme text-theme-text px-3 py-2 text-sm"
              data-testid="report-issue-category"
            >
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-theme-text text-sm">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of the issue"
              className="bg-theme/50 border-theme text-theme-text"
              maxLength={200}
              data-testid="report-issue-subject"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-theme-text text-sm">Description</Label>
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What happened? What did you expect? Any steps to reproduce?"
              className="w-full rounded-lg bg-theme/50 border border-theme text-theme-text px-3 py-2 text-sm resize-none"
              maxLength={5000}
              data-testid="report-issue-message"
            />
            <p className="text-[11px] text-theme-muted">{message.length}/5000 · We'll include the page URL automatically.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-theme">
          <Button variant="outline" onClick={onClose} disabled={loading} className="border-theme text-theme-muted">Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-[#002FA7] hover:bg-[#0055FF] text-white gap-2" data-testid="report-issue-submit">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Submit</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
